import type { IconName } from "@/components/icons/Icon";
import type { NotebookExposedVariable } from "@/components/notebook/notebook-types";

// The entities the agent prompt bar can @-mention. In a notebook: cells,
// tables (dataframe variables in kernel scope), and other kernel variables,
// built by the notebook editor. Outside a notebook (the new-conversation
// hero, the template builder, the docked sidebar), the environment's
// entities instead: features, resolvers, named queries, datasets, registry
// models, compute functions, scaling groups, and SQL catalog tables.
// Targets are built by the mounting surface and searched/rendered here.
//
// The kinds past `scaling-group` are reachable only through the compose bar's
// "+ Context" tray (see agent-context-items.ts), not by typing `@`: they
// address artifacts by opaque id (an operation id, a deployment id, a chart
// id, a monitor id) or by a composed query (`logs`), which nobody types from
// memory. They share this kind vocabulary so an attached artifact serializes,
// renders, and hovercards as the same pill an @-mention produces.

export type NotebookAgentMentionKind =
  | "cell"
  | "table"
  | "variable"
  | "input"
  | "feature"
  | "resolver"
  | "query"
  | "dataset"
  | "model"
  | "function"
  | "scaling-group"
  | "sql-catalog"
  | "sql-schema"
  | "sql-table"
  | "worksheet"
  | "notebook"
  | "online-query"
  | "offline-query"
  | "deployment"
  | "chart"
  | "dashboard"
  | "monitor"
  | "logs";

export interface NotebookAgentMentionTarget {
  kind: NotebookAgentMentionKind;
  /** Stable identity within a section: the cell id for cells, the bound
   *  variable name for tables/variables. */
  id: string;
  name: string;
  icon: IconName;
  /** Secondary text on the menu row (cell kind, python type, or shape). */
  detail?: string;
  /** Cell that defines this target; pill clicks scroll to it when set. */
  cellId?: string;
  /** Kernel variable backing a table/variable target; drives the hovercard. */
  variable?: NotebookExposedVariable;
  /** First lines of a cell target's source, shown on its hovercard. */
  sourcePreview?: string;
}

export interface NotebookAgentMentionSection {
  kind: NotebookAgentMentionKind;
  title: string;
  targets: NotebookAgentMentionTarget[];
}

// ---------------------------------------------------------------------------
// Mention tokens
//
// The wire/persisted form of a mention: `@[name](chalk://kind/id)`. This is
// what the agent model actually reads — the token names the entity AND carries
// its kind + stable id, so the agent can look it up through its tools (graph
// tools for features/resolvers/queries, notebook tools for cells/variables).
// UIs never show the raw token: the prompt bar serializes pills into tokens,
// and message/template renderers parse tokens back into pills.
// ---------------------------------------------------------------------------

const AGENT_MENTION_KINDS: readonly NotebookAgentMentionKind[] = [
  "cell",
  "table",
  "variable",
  "input",
  "feature",
  "resolver",
  "query",
  "dataset",
  "model",
  "function",
  "scaling-group",
  "sql-catalog",
  "sql-schema",
  "sql-table",
  "worksheet",
  "notebook",
  "online-query",
  "offline-query",
  "deployment",
  "chart",
  "dashboard",
  "monitor",
  "logs",
];

const isAgentMentionKind = (value: string): value is NotebookAgentMentionKind =>
  (AGENT_MENTION_KINDS as readonly string[]).includes(value);

// name: no `]` or newline; kind: lowercase word; id: no `)` or newline. Ids
// with those characters cannot round-trip and fall back to plain `@name` at
// serialization time.
const AGENT_MENTION_TOKEN_PATTERN = /@\[([^\]\n]+)\]\(chalk:\/\/([a-z-]+)\/([^)\n]+)\)/g;

// The id a `worksheet` mention carries for a scratch tab that has never been
// saved. Such a tab has no worksheet id, but it is still the thing the user is
// looking at, so it needs a stable token to be attachable.
export const UNSAVED_SQL_WORKSHEET_MENTION_ID = "unsaved";

const agentMentionIdSerializable = (target: Pick<NotebookAgentMentionTarget, "id" | "name">) =>
  !/[)\n]/.test(target.id) && !/[\]\n]/.test(target.name);

/** The wire form of one mention: `@[name](chalk://kind/id)`, or plain `@name`
 *  for the rare id/name that cannot round-trip through the token syntax. */
export const serializeAgentMentionToken = (
  target: Pick<NotebookAgentMentionTarget, "kind" | "id" | "name">,
): string =>
  agentMentionIdSerializable(target)
    ? `@[${target.name}](chalk://${target.kind}/${target.id})`
    : `@${target.name}`;

// ---------------------------------------------------------------------------
// Log-search mention ids
//
// Every other kind addresses an entity that already has an id. A `logs`
// mention instead carries a composed search — a query string plus the
// lookback it was written for — so the id is that pair, encoded to survive
// the token grammar. Parentheses are percent-encoded on top of
// encodeURIComponent (which leaves them literal) because log queries group
// with them, and an unescaped `)` would close the token early.
// ---------------------------------------------------------------------------

export interface AgentLogsMentionRef {
  /** The query exactly as the logs page's search bar takes it. */
  query: string;
  /** Lookback window as a plain duration (`15m`, `1h`, `24h`, `7d`). */
  lookback: string;
}

const encodeLogsMentionComponent = (value: string): string =>
  encodeURIComponent(value).replace(/[()]/g, (char) => (char === "(" ? "%28" : "%29"));

/** The `logs` mention id: `<encoded query>?since=<encoded lookback>`. The
 *  separator is unambiguous because the encoding escapes any literal `?`. */
export const serializeLogsMentionId = (ref: AgentLogsMentionRef): string =>
  `${encodeLogsMentionComponent(ref.query)}?since=${encodeLogsMentionComponent(ref.lookback)}`;

/** Inverse of serializeLogsMentionId. An id written by a newer client (or
 *  hand-edited) that does not carry a lookback decodes with an empty one
 *  rather than failing — the query is the part that matters. */
export const parseLogsMentionId = (id: string): AgentLogsMentionRef => {
  const separator = id.indexOf("?since=");
  const rawQuery = separator === -1 ? id : id.slice(0, separator);
  const rawLookback = separator === -1 ? "" : id.slice(separator + "?since=".length);
  const decode = (value: string): string => {
    try {
      return decodeURIComponent(value);
    } catch {
      // A malformed percent-sequence should degrade to the literal text, not
      // throw inside a render.
      return value;
    }
  };
  return { query: decode(rawQuery), lookback: decode(rawLookback) };
};

export interface AgentMentionTokenRef {
  kind: NotebookAgentMentionKind;
  id: string;
  name: string;
}

export type AgentMentionSegment =
  | { kind: "text"; text: string }
  | { kind: "mention"; mention: AgentMentionTokenRef };

/**
 * Splits text containing mention tokens into text/mention segments. Tokens
 * with an unknown kind stay literal text (forward compatibility: an older
 * client rendering a newer kind degrades to the readable token, never drops
 * content).
 */
export const parseAgentMentionSegments = (text: string): AgentMentionSegment[] => {
  const segments: AgentMentionSegment[] = [];
  let lastIndex = 0;
  for (const match of text.matchAll(AGENT_MENTION_TOKEN_PATTERN)) {
    const [token, name, kind, id] = match;
    if (!isAgentMentionKind(kind)) {
      continue;
    }
    if (match.index > lastIndex) {
      segments.push({ kind: "text", text: text.slice(lastIndex, match.index) });
    }
    segments.push({ kind: "mention", mention: { kind, id, name } });
    lastIndex = match.index + token.length;
  }
  if (lastIndex < text.length) {
    segments.push({ kind: "text", text: text.slice(lastIndex) });
  }
  return segments;
};

/** Replaces each mention token with its display form `@name` — for plain-text
 *  derivations of annotated text (conversation titles, previews). */
export const stripAgentMentionTokens = (text: string): string =>
  parseAgentMentionSegments(text)
    .map((segment) => (segment.kind === "text" ? segment.text : `@${segment.mention.name}`))
    .join("");

// Pill icon for a mention known only by kind — a token rehydrated when no
// live target with the same kind+id exists anymore (deleted cell, older
// deployment's feature), or a token rendered inside message history.
const AGENT_MENTION_FALLBACK_ICONS: Record<NotebookAgentMentionKind, IconName> = {
  cell: "notebook",
  table: "table",
  variable: "variable",
  input: "variable",
  feature: "feature",
  resolver: "resolver",
  query: "docs",
  dataset: "database",
  model: "box",
  function: "squareFunction",
  "scaling-group": "cpu",
  "sql-catalog": "database",
  "sql-schema": "schema",
  "sql-table": "table",
  worksheet: "sqlFile",
  notebook: "notebook",
  "online-query": "online",
  "offline-query": "circlePile",
  deployment: "rocket",
  chart: "chart",
  dashboard: "grid",
  monitor: "notifications",
  logs: "horizontalListQuery",
};

export const agentMentionKindIcon = (kind: NotebookAgentMentionKind): IconName =>
  AGENT_MENTION_FALLBACK_ICONS[kind];

/**
 * Resolves a parsed token back to a mention target: the live target with the
 * same kind+id when one exists (bringing its icon, detail, and hovercard),
 * otherwise a minimal target built from the token itself.
 */
export const agentMentionTargetFromToken = (
  ref: AgentMentionTokenRef,
  targets: readonly NotebookAgentMentionTarget[],
): NotebookAgentMentionTarget =>
  targets.find((target) => target.kind === ref.kind && target.id === ref.id) ?? {
    kind: ref.kind,
    id: ref.id,
    name: ref.name,
    icon: AGENT_MENTION_FALLBACK_ICONS[ref.kind],
  };

const MENTION_SECTION_ORDER: readonly { kind: NotebookAgentMentionKind; title: string }[] = [
  // Template inputs first: they only exist in the template builder, where a
  // typed `@` most likely reaches for one of the template's own parameters.
  { kind: "input", title: "Inputs" },
  { kind: "cell", title: "Cells" },
  { kind: "table", title: "Tables" },
  { kind: "variable", title: "Variables" },
  { kind: "feature", title: "Features" },
  { kind: "resolver", title: "Resolvers" },
  { kind: "query", title: "Named queries" },
  { kind: "dataset", title: "Datasets" },
  { kind: "model", title: "Models" },
  { kind: "function", title: "Compute functions" },
  { kind: "scaling-group", title: "Scaling groups" },
  { kind: "sql-catalog", title: "SQL catalogs" },
  { kind: "sql-schema", title: "SQL schemas" },
  { kind: "sql-table", title: "SQL catalog" },
  { kind: "notebook", title: "Notebooks" },
  { kind: "online-query", title: "Online queries" },
  { kind: "offline-query", title: "Offline queries" },
  { kind: "deployment", title: "Deployments" },
  { kind: "chart", title: "Metric charts" },
  { kind: "dashboard", title: "Dashboards" },
  { kind: "monitor", title: "Monitors" },
  { kind: "logs", title: "Log searches" },
];

// A match index starts a "word" when it is the first character or follows a
// separator/camelCase boundary — such matches read as intentional and score
// above mid-word hits.
const isMentionWordBoundary = (candidate: string, index: number): boolean => {
  if (index === 0) {
    return true;
  }
  const previous = candidate[index - 1];
  if (/[\s_\-./]/.test(previous)) {
    return true;
  }
  return /[a-z0-9]/.test(previous) && /[A-Z]/.test(candidate[index]);
};

/**
 * Case-insensitive fuzzy subsequence score: null when `query` is not a
 * subsequence of `candidate`, otherwise a score where consecutive runs,
 * word-boundary hits, prefixes, and exact matches rank above scattered
 * matches. An empty query matches everything with score 0.
 */
export const fuzzyMentionMatchScore = (query: string, candidate: string): number | null => {
  const normalizedQuery = query.trim().toLowerCase();
  if (normalizedQuery.length === 0) {
    return 0;
  }
  const normalizedCandidate = candidate.toLowerCase();
  let score = 0;
  let searchFrom = 0;
  let previousMatchIndex = -2;
  for (const queryChar of normalizedQuery) {
    const matchIndex = normalizedCandidate.indexOf(queryChar, searchFrom);
    if (matchIndex === -1) {
      return null;
    }
    score += 1;
    if (matchIndex === previousMatchIndex + 1) {
      score += 4;
    }
    if (isMentionWordBoundary(candidate, matchIndex)) {
      score += 6;
    }
    // Cap the gap penalty so one long skip does not drown the bonuses that
    // distinguish match quality.
    score -= Math.min(matchIndex - searchFrom, 3);
    previousMatchIndex = matchIndex;
    searchFrom = matchIndex + 1;
  }
  if (normalizedCandidate.startsWith(normalizedQuery)) {
    score += 12;
  }
  if (normalizedCandidate === normalizedQuery) {
    score += 20;
  }
  return score;
};

/**
 * Filters and ranks mention targets for the `@` autocomplete: one section per
 * kind (cells, then tables, then variables), each holding its fuzzy matches
 * sorted best-first. Sections with no matches are omitted. Ties keep the
 * caller's original target order (document order for cells).
 *
 * `perSectionLimit` truncates each section after ranking — environment
 * surfaces can offer hundreds of targets per kind (SQL tables, features),
 * and the menu should show the best few of every kind rather than all of
 * one. Typing narrows the query; the full set is never needed at once.
 *
 * `totalLimit` caps the number of targets across ALL sections, applied in
 * section order after per-section ranking: earlier sections keep their best
 * matches, the section that crosses the cap is truncated, and later sections
 * are dropped entirely.
 */
export const searchNotebookAgentMentionTargets = (
  targets: readonly NotebookAgentMentionTarget[],
  query: string,
  options?: { perSectionLimit?: number; totalLimit?: number },
): NotebookAgentMentionSection[] => {
  const sections = MENTION_SECTION_ORDER.flatMap(({ kind, title }) => {
    const matches = targets
      .filter((target) => target.kind === kind)
      .flatMap((target) => {
        const score = fuzzyMentionMatchScore(query, target.name);
        return score == null ? [] : [{ score, target }];
      })
      // Array.prototype.sort is stable, so equal scores keep document order.
      .sort((a, b) => b.score - a.score)
      .slice(0, options?.perSectionLimit)
      .map((match) => match.target);
    return matches.length > 0 ? [{ kind, title, targets: matches }] : [];
  });
  const totalLimit = options?.totalLimit;
  if (totalLimit == null) {
    return sections;
  }
  const capped: NotebookAgentMentionSection[] = [];
  let remaining = totalLimit;
  for (const section of sections) {
    if (remaining <= 0) {
      break;
    }
    const kept = section.targets.slice(0, remaining);
    remaining -= kept.length;
    capped.push({ ...section, targets: kept });
  }
  return capped;
};
